import React from 'react';
import classNames from 'classnames';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import {
    QuickPickNamePlace,
    QuickPickNameRunner,
    QuickPickNameRunnerTitle,
    QuickPickNameSilk,
    QuickPickNameStyle,
    QuickPickNameTitle,
    QuickPickSilk,
    QuickPickSilkHrWrapper,
} from 'src/domains/sportsbook/webview/components/quickPick/QuickPick.style';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { Trap } from 'src/domains/sportsbook/webview/components/trap/Trap';
import { SelectionId } from 'src_common/common/websocket2/id/WebsocketId';
import {
    RunnerDetails,
    RunnerDetailsWrapper,
    RunnerNameAndPlace,
} from 'src/domains/sportsbook/webview/components/raceSummary/templates/runnerInfo/JockeyAndTrainerDetails.style';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

interface PropsType {
    race: EventModel | undefined;
    market: MarketModel;
    sort: (selections: Array<SelectionModel>) => Array<SelectionModel>;
    hoveredSelection: SelectionId | null;
    sport: string | undefined;
}

export const QuickPickName = observer('QuickPickName', (props: PropsType) => {
    const { market, race, sort, sport, hoveredSelection } = props;
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;

    const isHorseracing = sport === 'horseracing';
    const selections = sort(market.selections.filter((x: SelectionModel) => x.display));
    const { sportsbookVariant } = configState.componentVariants;

    return (
        <QuickPickNameStyle variant={sportsbookVariant}>
            <QuickPickNameTitle>
                {isHorseracing && <QuickPickNamePlace variant={sportsbookVariant}>#</QuickPickNamePlace>}
                <QuickPickNameSilk variant={sportsbookVariant}>
                    {isHorseracing ? (
                        <I18n
                            langKey='events.quick-pick.silk.label'
                            defaultText='Silk'
                        />
                    ) : (
                        <I18n
                            langKey='events.quick-pick.trap.label'
                            defaultText='Trap'
                        />
                    )}
                </QuickPickNameSilk>
                <QuickPickNameRunnerTitle variant={sportsbookVariant}>
                    {isHorseracing ? (
                        <I18n
                            langKey='events.quick-pick.runner-jockey-trainer.label'
                            defaultText='Runner, Jockey, Trainer'
                        />
                    ) : (
                        <I18n
                            langKey='events.quick-pick.runner.label'
                            defaultText='Runner'
                        />
                    )}
                </QuickPickNameRunnerTitle>
            </QuickPickNameTitle>

            {selections.map((selection: SelectionModel) => {
                const isUnnamedFavourite = selection.templateId === 'unnamed-favourite';
                const nonRunner = selection.resultType === 'void';
                const jockey = (selection.metaDataJockey ?? '').trim();
                const trainer = (selection.metaDataTrainer ?? '').trim();
                const number = selection.metaDataNumber;
                const drawn = selection.metaDataDrawn;
                const courseType = race?.feedCourseType;

                const silkUrl = selection.metaDataSilkUrl ?? '';
                const silkAltDesc = selection.metaDataSilk ?? 'Silk image';

                const cls = classNames({
                    'quick-pick__name--nr': nonRunner,
                    hovered: selection.id2 === hoveredSelection,
                });

                return (
                    <li
                        key={selection.id}
                        className={cls}
                    >
                        {isUnnamedFavourite || !isHorseracing ? undefined : (
                            <QuickPickNamePlace variant={sportsbookVariant}>
                                {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
                                {number ? number : 'n/a'}
                                <br />
                                {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
                                {drawn && courseType === 'Flat' ? `(${drawn})` : undefined}
                            </QuickPickNamePlace>
                        )}

                        {isUnnamedFavourite ? null : isHorseracing ? (
                            <QuickPickSilkHrWrapper variant={sportsbookVariant}>
                                <QuickPickSilk
                                    imgBase={{
                                        src: silkUrl,
                                        optimizeOptions: {
                                            quality: 85,
                                            width: 48,
                                        },
                                    }}
                                    alt={silkAltDesc}
                                />
                            </QuickPickSilkHrWrapper>
                        ) : (
                            <QuickPickNameSilk variant={sportsbookVariant}>
                                <Trap
                                    trapNumber={selection.metaDataNumber}
                                    selectionId={selection.id2}
                                />
                            </QuickPickNameSilk>
                        )}

                        <QuickPickNameRunner variant={sportsbookVariant}>
                            {selection.name === '' ? (
                                'n/a'
                            ) : (
                                <RunnerNameAndPlace textTruncate>{selection.name}</RunnerNameAndPlace>
                            )}
                            {isUnnamedFavourite || !isHorseracing ? undefined : (
                                <RunnerDetailsWrapper
                                    data-test='runner-description'
                                    textTruncate={true}
                                >
                                    {jockey === '' && trainer === '' ? (
                                        <RunnerDetails title='n/a'>n/a</RunnerDetails>
                                    ) : (
                                        <>
                                            {jockey !== '' && <RunnerDetails title={jockey}>{jockey}</RunnerDetails>}
                                            {trainer !== '' && <RunnerDetails title={trainer}>{trainer}</RunnerDetails>}
                                        </>
                                    )}
                                </RunnerDetailsWrapper>
                            )}
                        </QuickPickNameRunner>
                    </li>
                );
            })}
        </QuickPickNameStyle>
    );
});
